import { useMemo, useState } from 'react'
import { Link, useParams, Navigate } from 'react-router-dom'
import {
  Mic,
  MessageCircle,
  MoreHorizontal,
  Pencil,
  RefreshCw,
  Users,
  Phone,
  CalendarClock,
  CheckSquare2
} from 'lucide-react'
import { Sidebar } from '../components/Sidebar'
import { AssignmentTimeline } from '../components/AssignmentTimeline'
import { AssignmentChangeModal } from '../components/AssignmentChangeModal'
import { AudioUploadButton } from '../components/AudioUploadButton'
import { CallList } from '../components/CallList'
import { LiverActivityTimeline } from '../components/LiverActivityTimeline'
import { LiverFormModal } from '../components/LiverFormModal'
import { LineInsightsPanel } from '../components/LineInsightsPanel'
import { LiverOverviewSummary } from '../components/LiverOverviewSummary'
import {
  getNextFollow,
  getRecentTodos,
  type NextFollowInsight,
  type TodoInsight
} from '../lib/callInsights'
import { useLiver, useUpdateLiver } from '../lib/useLivers'
import type { LiverFormValues } from '../lib/liverForm'
import {
  useLiverAssignments,
  useCurrentAssignment
} from '../lib/useAssignments'
import { useLiverCalls, useRetryFailedCall, useUpdateCallNextFollow, useUpdateCallTodos } from '../lib/useCalls'
import { useLineImports } from '../lib/useLineImports'
import { openRecorderForLiver } from '../lib/recorderLaunch'
import { isAudioUploadEnabled } from '../lib/prerelease'
import { buildLiverActivityItems } from '../lib/liverActivity'
import { buildLiverOverview } from '../lib/liverOverview'
import { actionVisibilityClass } from '../lib/responsiveUi'
import { hasInternalApi } from '../lib/internalApi'

export function LiverDetailPage() {
  const { liverId } = useParams<{ liverId: string }>()
  const liver = useLiver(liverId)
  const assignments = useLiverAssignments(liverId)
  const current = useCurrentAssignment(liverId)
  const calls = useLiverCalls(liverId)
  const lineImports = useLineImports(liverId)
  const retryFailedCall = useRetryFailedCall(liverId)
  const updateLiver = useUpdateLiver()
  const updateCallTodos = useUpdateCallTodos(liverId)
  const updateCallNextFollow = useUpdateCallNextFollow(liverId)
  const nextFollow = getNextFollow(calls.data ?? [])
  const recentTodos = getRecentTodos(calls.data ?? [], 5)
  const lineImportsEnabled = hasInternalApi()
  const audioUploadEnabled = isAudioUploadEnabled(import.meta.env)
  const overview = useMemo(
    () =>
      buildLiverOverview({
        currentAssignment: current,
        calls: calls.data ?? [],
        lineImports: lineImports.data
      }),
    [calls.data, current, lineImports.data]
  )
  const activityItems = useMemo(
    () => buildLiverActivityItems(calls.data ?? [], assignments.data ?? []),
    [assignments.data, calls.data]
  )
  const [changeOpen, setChangeOpen] = useState(false)
  const [assignmentDraft, setAssignmentDraft] = useState<{
    reason: string
    notes: string
  } | null>(null)
  const [editOpen, setEditOpen] = useState(false)
  const [recorderLaunchMessage, setRecorderLaunchMessage] = useState<string | null>(null)
  const selectedLiverName = liver.data?.display_name ?? null

  async function handleUpdateLiver(values: LiverFormValues) {
    if (!liverId) return
    await updateLiver.mutateAsync({ id: liverId, values })
    setEditOpen(false)
  }

  function openManualAssignmentChange() {
    setAssignmentDraft(null)
    setChangeOpen(true)
  }

  function openAssignmentChangeFromLine(handoffNote: string) {
    setAssignmentDraft({
      reason: '担当再編',
      notes: handoffNote
    })
    setChangeOpen(true)
  }

  if (!liverId) return <Navigate to="/" replace />

  return (
    <div className="min-h-full flex">
      <Sidebar />

      <main className="flex min-w-0 flex-1 flex-col overflow-visible pt-14 md:pt-0 xl:flex-row xl:overflow-hidden">
        {/* Center column */}
        <div className="min-w-0 flex-1 space-y-5 overflow-visible p-4 sm:space-y-6 sm:p-6 xl:overflow-y-auto">
          {/* Header card */}
          <section className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-6">
            {liver.isLoading ? (
              <div className="text-sm text-gray-400">読み込み中…</div>
            ) : !liver.data ? (
              <div className="text-sm text-red-500">
                ライバーが見つかりません
              </div>
            ) : (
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-5">
                {liver.data.avatar_url ? (
                  <img
                    src={liver.data.avatar_url}
                    alt={liver.data.display_name}
                    className="h-14 w-14 rounded-full object-cover bg-gray-100 sm:h-16 sm:w-16"
                  />
                ) : (
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-sky-100 to-violet-100 text-2xl font-semibold text-sky-700 sm:h-16 sm:w-16">
                    {liver.data.display_name.slice(0, 1)}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h1 className="text-xl font-bold text-gray-900">
                    {liver.data.display_name}
                  </h1>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                    <StatusPill status={liver.data.status} />
                    {liver.data.joined_at && (
                      <span>
                        入所{' '}
                        {new Date(liver.data.joined_at).toLocaleDateString('ja-JP')}
                      </span>
                    )}
                    {liver.data.line_user_id && (
                      <span className="text-gray-400 font-mono">
                        LINE: {liver.data.line_user_id}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end shrink-0">
                  {audioUploadEnabled && (
                    <div className={actionVisibilityClass('upload')}>
                      <AudioUploadButton liverId={liverId} />
                    </div>
                  )}
                  <button
                    className={`${actionVisibilityClass('record')} items-center gap-1.5 px-3 py-1.5 bg-sky-500 text-white text-sm rounded-lg hover:bg-sky-600`}
                    title="LogManeRecorderを開いてこのライバーを選択します"
                    onClick={() => {
                      void openRecorderForLiver({
                        liverId,
                        liverName: selectedLiverName
                      }).then((result) => {
                        setRecorderLaunchMessage(
                          result === 'local'
                            ? 'LogMane Recorder にライバーを送信しました。メニューバーで録音を開始できます。'
                            : 'Chromeの確認が出たら LogMane Recorder を開いてください。メニューバーで録音を開始できます。'
                        )
                      })
                    }}
                  >
                    <Mic className="w-4 h-4" />
                    Recorderで録音
                  </button>
                  <button
                    className={`${actionVisibilityClass('line')} items-center gap-1.5 px-3 py-1.5 border border-gray-200 text-gray-700 text-sm rounded-lg opacity-50 cursor-not-allowed`}
                    disabled
                    title="LINE 連携は次フェーズで実装"
                  >
                    <MessageCircle className="w-4 h-4" />
                    LINE 送信
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditOpen(true)}
                    className="p-1.5 border border-gray-200 hover:bg-gray-50 text-gray-500 rounded-lg"
                    title="ライバーを編集"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    className="p-1.5 border border-gray-200 hover:bg-gray-50 text-gray-500 rounded-lg"
                    title="その他"
                  >
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
            {recorderLaunchMessage && (
              <div className="mt-4 rounded-lg border border-sky-100 bg-sky-50 px-3 py-2 text-xs leading-relaxed text-sky-700">
                <p>{recorderLaunchMessage}</p>
                <Link to="/settings#recorder-download" className="mt-1 inline-flex font-semibold underline">
                  未インストールの場合はRecorderをダウンロード
                </Link>
              </div>
            )}
          </section>

          <LiverOverviewSummary
            overview={overview}
            loading={liver.isLoading || calls.isLoading || assignments.isLoading}
            lineImportsLoading={lineImportsEnabled && lineImports.isLoading}
            lineImportsError={lineImports.error instanceof Error ? lineImports.error : null}
            lineImportsEnabled={lineImportsEnabled}
          />

          {liver.data && (
            <LineInsightsPanel
              liverId={liverId}
              liverName={liver.data.display_name}
              onRequestAssignmentChange={openAssignmentChangeFromLine}
            />
          )}

          <section className="space-y-3">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-sm font-semibold text-gray-700">タイムライン</h2>
                <p className="mt-1 text-xs text-gray-400">
                  通話と担当変更を同じ時系列で確認できます。
                </p>
              </div>
              <span className="text-[11px] text-gray-400">{activityItems.length}件</span>
            </div>
            <LiverActivityTimeline
              items={activityItems}
              loading={calls.isLoading || assignments.isLoading}
            />
          </section>

          {/* Calls (Phase α: 通話パイプライン稼働中) */}
          <section className="space-y-3">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                <Phone className="w-4 h-4 text-gray-400" />
                通話履歴
              </h2>
              <span className="text-[11px] text-gray-400">
                {calls.data?.length ?? 0} 件 · Gateway が議事録生成
              </span>
            </div>
            <CallList
              calls={calls.data ?? []}
              loading={calls.isLoading}
              error={(calls.error as Error) ?? null}
              onRetry={(call) => retryFailedCall.mutateAsync(call)}
              onUpdateTodos={(callId, todos) => updateCallTodos.mutateAsync({ callId, todos })}
              onUpdateNextFollow={(callId, nextFollowDate) =>
                updateCallNextFollow.mutateAsync({ callId, nextFollowDate })
              }
            />
          </section>
        </div>

        {/* Right rail */}
        <aside className="w-full border-t border-gray-200 bg-gray-50 p-4 space-y-4 xl:w-80 xl:border-l xl:border-t-0 xl:overflow-y-auto">
          {/* Assignment history card */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-gray-700 flex items-center gap-1.5 uppercase tracking-wide">
                <Users className="w-3 h-3 text-gray-400" />
                担当履歴
              </h3>
              {assignments.data && assignments.data.length > 0 && (
                <span className="text-[10px] text-gray-400">
                  {assignments.data.length}件
                </span>
              )}
            </div>

            {assignments.isLoading ? (
              <div className="text-xs text-gray-400 py-2">読み込み中…</div>
            ) : assignments.error ? (
              <div className="text-xs text-red-500 py-2">
                {String(assignments.error)}
              </div>
            ) : (
              <AssignmentTimeline assignments={assignments.data ?? []} />
            )}

            <button
              onClick={openManualAssignmentChange}
              className="mt-4 w-full flex items-center justify-center gap-1.5 px-3 py-2 border border-gray-200 hover:bg-gray-50 text-sm text-gray-700 rounded-lg"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              {current ? '担当を切り替える' : '初回担当をアサイン'}
            </button>
          </div>

          <NextFollowCard follow={nextFollow} loading={calls.isLoading} />
          <TodoInsightsCard todos={recentTodos} loading={calls.isLoading} />
          <PlaceholderCard title="KPI" hint="配信プラットフォーム連携後" />
        </aside>
      </main>

      {liver.data && (
        <AssignmentChangeModal
          liverId={liverId}
          liverName={liver.data.display_name}
          currentAssignment={current}
          open={changeOpen}
          initialReason={assignmentDraft?.reason}
          initialNotes={assignmentDraft?.notes}
          onClose={() => setChangeOpen(false)}
        />
      )}
      {liver.data && (
        <LiverFormModal
          open={editOpen}
          mode="edit"
          initialLiver={liver.data}
          loading={updateLiver.isPending}
          error={updateLiver.error instanceof Error ? updateLiver.error.message : null}
          onClose={() => setEditOpen(false)}
          onSubmit={handleUpdateLiver}
        />
      )}
    </div>
  )
}

function NextFollowCard({
  follow,
  loading
}: {
  follow: NextFollowInsight | null
  loading: boolean
}) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4">
      <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-1.5">
        <CalendarClock className="w-3 h-3 text-gray-400" />
        次回フォロー
      </h3>
      {loading ? (
        <div className="text-xs text-gray-400 py-1">読み込み中…</div>
      ) : follow ? (
        <div className="space-y-1.5">
          <div className="flex items-baseline gap-2">
            <span className="text-lg font-semibold text-gray-900">
              {formatDateLabel(follow.date)}
            </span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${followTone(follow.state)}`}>
              {followLabel(follow.state)}
            </span>
          </div>
          <p className="text-[11px] text-gray-400">
            直近の議事録から抽出されたフォロー予定です。
          </p>
        </div>
      ) : (
        <p className="text-[11px] text-gray-400">次回フォロー日はまだありません。</p>
      )}
    </div>
  )
}

function TodoInsightsCard({
  todos,
  loading
}: {
  todos: TodoInsight[]
  loading: boolean
}) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-1.5">
          <CheckSquare2 className="w-3 h-3 text-gray-400" />
          TODO
        </h3>
        {todos.length > 0 && <span className="text-[10px] text-gray-400">{todos.length}件</span>}
      </div>
      {loading ? (
        <div className="text-xs text-gray-400 py-1">読み込み中…</div>
      ) : todos.length > 0 ? (
        <ul className="space-y-2">
          {todos.map((todo, index) => (
            <li key={`${todo.callId}-${index}`} className="text-[12px] text-gray-700">
              <div className="flex items-start gap-2">
                <span
                  className={`mt-0.5 shrink-0 text-[10px] px-1.5 py-0.5 rounded uppercase font-medium ${
                    todo.assignee === 'manager'
                      ? 'bg-sky-100 text-sky-700'
                      : 'bg-rose-100 text-rose-700'
                  }`}
                >
                  {todo.assignee === 'manager' ? 'マネ' : 'ライバー'}
                </span>
                <div className="min-w-0">
                  <p className="break-words">{todo.task}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    {formatCalledAt(todo.calledAt)}
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[11px] text-gray-400">抽出済みTODOはまだありません。</p>
      )}
    </div>
  )
}

function StatusPill({ status }: { status: string }) {
  const styles = (() => {
    switch (status) {
      case 'active':
        return 'bg-emerald-50 text-emerald-700 border-emerald-100'
      case 'inactive':
        return 'bg-amber-50 text-amber-700 border-amber-100'
      case 'graduated':
        return 'bg-gray-100 text-gray-600 border-gray-200'
      default:
        return 'bg-gray-50 text-gray-500 border-gray-200'
    }
  })()
  const label = status === 'active' ? 'Active' : status === 'inactive' ? 'Inactive' : '卒業'
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 text-[10px] uppercase font-medium rounded border ${styles}`}
    >
      {label}
    </span>
  )
}

function PlaceholderCard({ title, hint }: { title: string; hint: string }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4">
      <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
        {title}
      </h3>
      <p className="text-[11px] text-gray-400 italic">{hint}</p>
    </div>
  )
}

function formatDateLabel(date: string): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    weekday: 'short'
  })
}

function formatCalledAt(calledAt: string): string {
  return new Date(calledAt).toLocaleDateString('ja-JP', {
    month: 'numeric',
    day: 'numeric'
  })
}

function followLabel(state: NextFollowInsight['state']): string {
  switch (state) {
    case 'today':
      return '今日'
    case 'overdue':
      return '要確認'
    default:
      return '予定'
  }
}

function followTone(state: NextFollowInsight['state']): string {
  switch (state) {
    case 'today':
      return 'bg-sky-50 text-sky-700'
    case 'overdue':
      return 'bg-red-50 text-red-700'
    default:
      return 'bg-emerald-50 text-emerald-700'
  }
}
